import { z } from "zod";
import { memoize } from "es-toolkit";
import escapeStringRegexp from "escape-string-regexp";
const tooWeak = {
    score: 0,
    value: "Too weak",
    minDiversity: 0,
    minLength: 0,
};
const weak = {
    score: 1,
    value: "Weak",
    minDiversity: 2,
    minLength: 8,
};
const medium = {
    score: 2,
    value: "Medium",
    minDiversity: 3,
    minLength: 10,
};
const strong = {
    score: 3,
    value: "Strong",
    minDiversity: 4,
    minLength: 12,
};
const options = [
    tooWeak,
    weak,
    medium,
    strong,
];
const owaspSymbols = "!\"#$%&'()*+,-./\\:;<=>?@[]^_`{|}~";
const rules = [
    {
        key: "lowercase",
        regex: new RegExp("[a-z]"),
    },
    {
        key: "uppercase",
        regex: new RegExp("[A-Z]"),
    },
    {
        key: "number",
        regex: new RegExp("[0-9]"),
    },
    {
        key: "symbol",
        regex: new RegExp(`[${escapeStringRegexp(owaspSymbols)}]`),
    },
];
/**
 * Calculate password strength
 * @see Based on https://github.com/deanilvincent/check-password-strength/tree/master
 */
const passwordStrength = (password = "") => {
    const strength = {
        length: password.length,
        contains: rules
            .filter(({ regex }) => regex.test(password))
            .map(({ key }) => key),
    };
    const fulfilledOption = options
        .filter(option => strength.contains.length >= option.minDiversity)
        .filter(option => strength.length >= option.minLength)
        .sort((o1, o2) => o2.score - o1.score)
        .map(option => ({
        score: option.score,
        value: option.value,
    }))
        .at(0) ?? tooWeak;
    return {
        ...strength,
        ...fulfilledOption,
    };
};
/**
 * Memoized function to evaluate password using zxcvbn
 */
export const evaluatePass = memoize((password) => passwordStrength(password));
/**
 * SuperRefine zod function for password complexity
 * Error on Weak and Too weak passwords
 */
export const enforcePasswordComplexity = (message = "Password is too weak") => (ctx) => {
    const { value: password } = ctx;
    const { score, } = evaluatePass(password);
    if (score <= 1) {
        ctx.issues.push({
            code: "custom",
            message,
            fatal: true,
            input: password,
        });
    }
};
/**
 * Used when registering new user or setting new password
 * Since score depends on library implementation this cannot be used to validate pre-existing passwords
 */
export const PasswordSchema = z
    .string()
    .check(enforcePasswordComplexity());
//# sourceMappingURL=password.js.map